import './date-range-picker-field.scss';

import { DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import CalendarIcon from '@/media/icons/calendar.svg?react';

import MaskedInput, { type MaskedInputInterface } from '../masked-input';
import OutsideClickHandler from '../outside-click-handler';

dayjs.extend(customParseFormat);

type ChangedMaskedInputInterface = Omit<
  MaskedInputInterface,
  'value' | 'onChange' | 'mask'
>;

interface IProps extends ChangedMaskedInputInterface {
  value?: (string | undefined)[];
  onChange?: (value: (string | undefined)[]) => void;
  isFrozen?: boolean;
}
const { RangePicker } = DatePicker;

const DateRangePickerField = forwardRef<HTMLInputElement, IProps>(
  ({ value, onChange, isFrozen, ...rest }, forwardRef) => {
    const calendar = useRef<HTMLDivElement>(null);

    const [focused, setFocused] = useState(false);
    const [inputValue, setInputValue] = useState<string[]>(['', '']);

    const dates = useMemo(() => {
      if (!value) return [null, null];
      const [startDate, endDate] = value;
      return [
        dayjs(startDate).isValid() ? dayjs(startDate) : null,
        dayjs(endDate).isValid() ? dayjs(endDate) : null
      ];
    }, [value]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      const dates = newValue.split(' - ');
      onChange?.(
        dates.map((item) =>
          dayjs(item, 'DD.MM.YYYY').isValid()
            ? dayjs(item, 'DD.MM.YYYY')?.format('YYYY-MM-DD')
            : ''
        )
      );
      setInputValue(dates);
    };

    const handleCalendarChange = (
      dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null,
      _: [string, string],
      info: { range?: 'start' | 'end' } // Тип BaseInfo из rc-picker не импортируется
    ) => {
      if (!dates) return;
      const isValueValid = value?.every((item) => dayjs(item)?.isValid());
      if (info.range === 'start' && isValueValid) {
        dates = [dates[0], null];
      } else if (info.range === 'end' && isValueValid) {
        dates = [null, dates[1]];
      }

      onChange?.(
        dates?.map((item) => {
          const date = dayjs(item, 'DD.MM.YYYY');
          if (date.isValid()) return date?.format('YYYY-MM-DD');
          else return '';
        })
      );
      const isDatesValid = dates?.every((item) => dayjs(item)?.isValid());
      if (isDatesValid) setFocused(false);
    };

    useEffect(() => {
      if (value && dayjs(value[0]).isValid() && dayjs(value[1]).isValid()) {
        setInputValue(value.map((item) => dayjs(item).format('DD.MM.YYYY')));
      }
    }, [value]);

    return (
      <OutsideClickHandler
        onOutsideClick={(element) => {
          if (!calendar.current?.contains(element)) setFocused(false);
        }}
      >
        <div className="date-range-picker-field">
          <MaskedInput
            {...rest}
            type="tel"
            ref={forwardRef}
            value={inputValue[0] ? inputValue.join(' - ') : ''}
            onChange={handleInputChange}
            endContent={
              <span className="date-range-picker-field__icon">
                <CalendarIcon />
              </span>
            }
            onClick={(e) => {
              if (!isFrozen) setFocused((prev) => !prev);
              rest.onClick?.(e);
            }}
            mask="__.__.____ - __.__.____"
            replacement={rest.replacement || { _: /\d/ }}
            autoComplete="off"
            isFrozen={isFrozen}
          />

          <div ref={calendar}>
            <RangePicker
              picker="date"
              value={dates as [Dayjs | null, Dayjs | null]}
              onCalendarChange={handleCalendarChange}
              getPopupContainer={() => calendar.current || document.body}
              className="date-range-picker-field__calendar"
              classNames={{
                popup: {
                  root: 'date-range-picker-field__calendar-dropdown'
                }
              }}
              format={'DD.MM.YYYY'}
              open={focused}
            />
          </div>
        </div>
      </OutsideClickHandler>
    );
  }
);

DateRangePickerField.displayName = 'DateRangePickerField';

export default DateRangePickerField;
